import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { dataEvents } from "@/data/events/dataEvents";
import type { ISelectedItem } from "@/data/model/Bill";
import type { IPaymentIntent } from "@/data/model/Customer";
import { BuyerRequirement, PaymentProvider, UserItemSelectionChangeReason } from "@/data/model/Enums";
import type { BillItemSelectionChangedEvent } from "@/data/model/Events";
import {
    getIsApplyVouchersAllowedForOrderUseCase,
    getIsApplyVouchersAllowedForPayUseCase,
} from "@/data/services/PaymentService";
import { exceptionTypes } from "@/data/util/Config";
import { Format } from "@/data/util/Format";
import { Misc } from "@/data/util/Misc";

import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";
import { Ripple } from "@/shared/ui/Ripple";
import { toMoneyWithSign } from "@/shared/utils/format";
import { type VueComponentInstance, createVueComponent } from "@/shared/utils/vue";

import ApplyDiscountSection from "@/components/vouchers/ApplyDiscountSection.vue";

import { config } from "@/app/App";
import { TastierClient } from "@/app/TastierClient";
import { PaymentState } from "@/app/context/PayContext";
import { BaseController } from "@/app/controller/BaseController";
import { EmailInput } from "@/app/elements/EmailInput";
import { NumberInput } from "@/app/elements/NumberInput";
import { PayButton } from "@/app/elements/PayButton";
import { PaymentMethodSelector } from "@/app/elements/PaymentMethodSelector";
import { QuickPaymentMethodSelector } from "@/app/elements/QuickPaymentMethodSelector";
import { Slider } from "@/app/elements/Slider";
import { trans } from "@/app/localization/Trans";
import { UserCreditCardsService } from "@/app/services/userCreditCardsService";
import { Helper } from "@/app/ui/Helper";
import { Popup } from "@/app/ui/Popup";
import { UserMenu } from "@/app/ui/UserMenu";

export class PayController extends BaseController {
    public onGoBack?: { () };

    private readonly $scrollable: JQuery;
    private readonly $tip: JQuery;
    private readonly $title: JQuery;

    private tipSlider?: Slider;
    private $tipInputRow?: JQuery;
    private tipInput?: NumberInput;
    private $receivedRow?: JQuery;
    private receivedInput?: NumberInput;
    private useTip: boolean = true;

    private methodSelector: PaymentMethodSelector;
    private quickPaymentMethodSelector: QuickPaymentMethodSelector;
    private $signinSection: JQuery;

    private $applyDiscountSectionContainer!: JQuery;
    private applyDiscountSectionComponent?: VueComponentInstance<typeof ApplyDiscountSection>;

    private $emailSection: JQuery;
    private emailInput: EmailInput;

    private $total: JQuery;
    private $consumptionAmt: JQuery;
    private $discountRow: JQuery;
    private $discountAmount: JQuery;
    private $tipAmt: JQuery;

    private $bottomContainer: JQuery;

    private payBtn: PayButton;
    public userMenu: UserMenu;
    public shouldShowSelectionsChangedPopupOnLeave: boolean = false;

    public $inputContainer: JQuery;

    private currentState?: {
        isPayService: boolean;
        consumption: number;
        discount: number;
        totalAfterDiscount: number;
        selectedItems: ISelectedItem[];
    };

    constructor() {
        super("pay");
        const hdr = $('<div class="head"></div>').appendTo(this.$view);
        this.$title = $('<div class="title"></div>').appendTo(hdr);

        this.updateTitle(TastierClient.payContext.isPayLater());

        Ripple.bind($('<a class="action left" href="#"><i class="fal fa-arrow-left"></i></a>'))
            .prependTo(hdr)
            .click((e) => {
                e.preventDefault();

                const statesToSkip = [PaymentState.Paying, PaymentState.Confirmed, PaymentState.Failed];
                if (statesToSkip.includes(TastierClient.payContext.state)) {
                    return;
                }

                if (this.onGoBack) {
                    this.onGoBack();
                }
            });

        this.userMenu = new UserMenu().appendTo(hdr);
        this.$scrollable = $('<div class="scrollable" />').appendTo(this.$view);
        this.$tip = $('<div class="tip" />').appendTo(this.$scrollable);

        // todo: what if the user changes location while using app? what if user logs out while using app? this tip slider is only created once, it should be verified on each visible change
        if (TastierClient.identityContext?.isWaiterInCurrentLocation()) {
            this.$tipInputRow = $(`<div class="row"><div class="label">${translations.pay.tip}</div></div>`).appendTo(
                this.$tip
            );

            this.tipInput = new NumberInput(undefined, {
                min: 0,
                decimals: 2,
                step: 0.01,
            }).renderTo(this.$tipInputRow);

            this.tipInput.onChange = (value) => {
                if (value! < 0) {
                    this.tipInput!.setValue(0);
                } else {
                    this.receivedInput!.setValue(value! + this.getConsumption());
                    TastierClient.payContext.setTip(value!);
                }

                this.resetSelectedPaymentMethodIfNeeded();
            };

            this.tipInput.onKeyPress = (value) => {
                if (value! >= 0) {
                    this.receivedInput!.setValue(value! + this.getConsumption());
                    TastierClient.payContext.setTip(value!);
                    this.resetSelectedPaymentMethodIfNeeded();
                }
            };

            this.tipInput.onFocus = (hasFocus) => {
                this.setEmailVisibility(hasFocus);
                this.setApplyDiscountSectionVisibility(hasFocus);
                this.quickPaymentMethodSelector.setVisibility(!hasFocus);
            };

            this.$receivedRow = $(
                `<div class="row"><div class="label">${translations.pay.received}</div></div>`
            ).appendTo(this.$tip);
            this.receivedInput = new NumberInput(undefined, {
                min: 0,
                max: 999999,
                decimals: 2,
                step: 0.01,
            }).renderTo(this.$receivedRow);

            this.receivedInput.onFocus = (hasFocus) => {
                this.setEmailVisibility(hasFocus);
                this.setApplyDiscountSectionVisibility(hasFocus);
                this.quickPaymentMethodSelector.setVisibility(!hasFocus);
            };

            this.receivedInput.onChange = (value) => {
                if (value! >= this.getConsumption()) {
                    this.tipInput!.setValue(value! - this.getConsumption());
                    TastierClient.payContext.setTip(this.tipInput!.getValue()!);
                    this.resetSelectedPaymentMethodIfNeeded();
                } else {
                    this.receivedInput!.setValue(this.getConsumption());
                }
            };
            this.receivedInput.onKeyPress = (value) => {
                if (value! >= this.getConsumption()) {
                    this.tipInput!.setValue(value! - this.getConsumption());
                    TastierClient.payContext.setTip(this.tipInput!.getValue()!);
                    this.resetSelectedPaymentMethodIfNeeded();
                }
            };
        } else {
            const tipRow = $(`<div class="row"><div class="label">${translations.pay.tip}</div></div>`).appendTo(
                this.$tip
            );

            this.tipSlider = new Slider(config.maxTip).setValue(TastierClient.payContext.tipPercent!).appendTo(tipRow);
            this.tipSlider.onChange = (v) => {
                TastierClient.payContext.setTipInPercent(v);
                this.resetSelectedPaymentMethodIfNeeded();
            };
        }

        this.$inputContainer = $('<div class="inputs-container"></div>').appendTo(this.$view);

        this.initializeApplyDiscountSection();

        this.$emailSection = $('<form class="email form" novalidate></form>').appendTo(this.$inputContainer);
        this.$emailSection.submit((e) => {
            e.preventDefault();
            this.onFormSubmit();
            return false;
        });

        const formRow = $('<div class="formrow"></div>').appendTo(this.$emailSection);
        this.emailInput = new EmailInput(false).appendTo(formRow);
        this.emailInput.onFocus = (hasFocus) => {
            if (hasFocus) {
                this.$tipInputRow?.hide();
                this.$receivedRow?.hide();
            } else {
                this.$tipInputRow?.show();
                this.$receivedRow?.show();
            }
        };

        this.$signinSection = $('<div class="signin" />').appendTo(this.$inputContainer);
        this.$signinSection.hide();

        $(`<button type="button" class="btn">${trans.pay.signup}</button>`)
            .appendTo(this.$signinSection)
            .click(() => {
                const inputUserEmail = this.emailInput.isValid() ? this.emailInput.getValue() : undefined;
                this.userMenu.showSignup(inputUserEmail);
            });

        this.$signinSection.append(`<div class="hint">${trans.pay.signupBenefits}</div>`);

        this.quickPaymentMethodSelector = new QuickPaymentMethodSelector().appendTo(this.$inputContainer);
        this.quickPaymentMethodSelector.onSelect = (selectedMethod) => {
            if (selectedMethod != null) {
                TastierClient.payContext.setPaymentMethod(selectedMethod, undefined);
                this.validateAndPay();
            }
        };

        this.$total = $('<div class="total"><div class="line" /></div>').appendTo(this.$inputContainer);
        this.$consumptionAmt = $(
            `<div class="row"><div class="lbl">${translations.pay.consumption}</div><div class="val"><span class="unit">CHF</span><span class="amt" /></div></div>`
        )
            .appendTo(this.$total)
            .find(".amt");

        this.$discountRow = $('<div class="row"></div>').appendTo(this.$total);
        this.$discountAmount = $(
            `<div class="lbl">${trans.pay.discount}</div><div class="val" style="color: #fd397a"><span class="unit">CHF</span><span class="amt" /></div>`
        )
            .appendTo(this.$discountRow)
            .find(".amt");

        this.$tipAmt = $(
            `<div class="row"><div class="lbl">${translations.pay.tip}</div><div class="val"><span class="unit">CHF</span><span class="amt" /></div></div>`
        )
            .appendTo(this.$total)
            .find(".amt");

        this.$bottomContainer = $('<div class="bottom" />').appendTo(this.$view);
        this.$bottomContainer.append(`<div class="disclaimer">${trans.pay.disclaimer}</div>`);

        const btnContainer = $('<div class="btncontainer" />').appendTo(this.$bottomContainer);
        this.methodSelector = new PaymentMethodSelector().appendTo(this.$bottomContainer);
        this.methodSelector.onCardFormDestroyed = () => {
            TastierClient.payContext.clearPaymentMethod();
        };

        this.methodSelector.onSelect = (method, withCardToken) => {
            if (method == null) {
                TastierClient.payContext.clearPaymentMethod();
                this.setApplyDiscountSectionVisibility();
            } else {
                const handler = TastierClient.payContext.setPaymentMethod(method, withCardToken);
                this.setApplyDiscountSectionVisibility();
                return handler;
            }
        };

        this.methodSelector.onModeChange = () => this.updateState();
        this.methodSelector.onOpenClose = () => this.updateState();

        this.payBtn = new PayButton(translations.pay.payNow).appendTo(btnContainer);
        this.payBtn.onClick = () => {
            this.onEnabledPayButtonClick();
        };
        this.payBtn.onDisabledClick = () => {
            this.onDisabledPayButtonClick();
        };
    }

    private resetSelectedPaymentMethodIfNeeded() {
        const selectPaymentMethod = TastierClient.payContext.selectedPaymentMethod;
        if (selectPaymentMethod != null && selectPaymentMethod.provider === PaymentProvider.Stripe) {
            this.methodSelector.reset();
        }
    }

    private initializeApplyDiscountSection() {
        const props = {
            enableQrScanner: true,
            orderServiceId: TastierClient.context.selectedServiceId,
            onFocus: (hasFocus: boolean) => {
                if (hasFocus) {
                    this.$tipInputRow?.hide();
                    this.$receivedRow?.hide();
                } else {
                    this.$tipInputRow?.show();
                    this.$receivedRow?.show();
                }
            },
            onApplyDiscountErrorOccured: (error: DaoError) => this.onApplyDiscountErrorOccured(error),
        };

        this.$applyDiscountSectionContainer = $("<div></div>").appendTo(this.$inputContainer);
        this.applyDiscountSectionComponent = createVueComponent(
            ApplyDiscountSection,
            props,
            this.$applyDiscountSectionContainer[0]
        ).mountedComponent;
    }

    private onApplyDiscountErrorOccured(error: DaoError) {
        if (
            error.type === exceptionTypes.voucherWithCodeNotExistsException ||
            error.type === exceptionTypes.discountWithCodeNotExistsException
        ) {
            Popup.show(trans.voucher.invalidCode);
        } else if (error.type === exceptionTypes.voucherExpiredException) {
            Popup.show(trans.voucher.codeExpired);
        } else if (error.type === exceptionTypes.voucherNotEnabledException) {
            Popup.show(trans.voucher.invalidCode);
        } else if (
            error.type === exceptionTypes.voucherNotApplicableException ||
            error.type === exceptionTypes.discountNotApplicableException
        ) {
            Popup.show(trans.voucher.codeNotApplicable);
        } else if (error.type === exceptionTypes.voucherUsedException) {
            Popup.show(trans.voucher.codeUsed);
        } else if (
            error.type === exceptionTypes.voucherCodeAlreadyAppliedToCartException ||
            error.type === exceptionTypes.voucherCodeAlreadyAppliedToBillException ||
            error.type === exceptionTypes.discountCodeAlreadyAppliedToCartException ||
            error.type === exceptionTypes.discountCodeAlreadyAppliedToBillException
        ) {
            Popup.show(trans.voucher.voucherCodeAlreadyApplied);
        }
    }

    private onFormSubmit() {
        if (!this.payBtn.isDisabled()) {
            this.onEnabledPayButtonClick();
        } else {
            this.onDisabledPayButtonClick();
        }
    }

    private async onEnabledPayButtonClick() {
        if (this.methodSelector.isCardFormMode) {
            try {
                await this.methodSelector.validateCardForm();
                this.validateAndPay();
            } catch (ex) {
                logError(ex);
            }
        } else {
            this.validateAndPay();
        }
    }

    private onDisabledPayButtonClick() {
        if (TastierClient.payContext.state === PaymentState.MethodSelection) {
            this.methodSelector.open();
        }
    }

    protected async onBecomeVisible(data?: { isMainNavigation: boolean; isForPayService: boolean }) {
        const isPayService = Helper.isPayService();
        const isInit = data?.isMainNavigation == true;

        // register event handlers
        TastierClient.payContext.onAmountChanged.add(this.updateAmount, this);
        TastierClient.payContext.onStateChanged.add(this.updateState, this);
        TastierClient.payContext.onIntentChanged.add(this.intentUpdated, this);
        TastierClient.identityContext.onIdentityChange.add(this.onAuthenticationChanged, this);
        UserCreditCardsService.onCreditCardsChanged.add(this.onCreditCardsChanged, this);

        // notify about a cart when user uses order service
        // notify about item selections changed when user uses pay service
        if (isPayService) {
            dataEvents.appSocket.ItemSelectionChanged.add(this.onItemSelectionChanged, this);
            TastierClient.payContext.onBillDiscountChanged.add(this.onBillDiscountChanged, this);
        } else {
            TastierClient.context.onCartChanged.add(this.onCartUpdated, this);
            TastierClient.context.onCartDiscountChanged.add(this.onCartVoucherApplied, this);
        }

        // // Setup the component: Apply discount
        this.applyDiscountSectionComponent?.reset();

        if (isPayService) {
            this.applyDiscountSectionComponent?.setBillId(TastierClient.context.currentBill!.id);
        } else {
            this.applyDiscountSectionComponent?.setCartId(TastierClient.context.getCurrentCartId());
        }

        this.setEmailVisibility();

        const isAnonymousUserEmailRequired = this.isAnonymousUserEmailRequired();
        const isEndUserEmailRequired = this.isEndUserEmailRequired();

        if (isAnonymousUserEmailRequired || isEndUserEmailRequired) {
            const email = TastierClient.identityContext.getProfile()?.email;
            if (isAnonymousUserEmailRequired && email !== undefined) {
                this.emailInput.setValue(email);
            } else {
                this.emailInput.setValue("");
            }
        }

        // if user didn't come from main view - no need to init state, only recover it
        if (isInit) {
            // set initial state - the state that user sees and amounts that user agreed to pay
            // this shouldn't change after user enters this page
            TastierClient.payContext.renewConsumption();
            this.currentState = {
                isPayService: isPayService,
                consumption: TastierClient.payContext.consumption,
                discount: TastierClient.payContext.discountAmount,
                totalAfterDiscount: TastierClient.payContext.totalExclTipAfterDiscounts,
                selectedItems: TastierClient.context.getSelectedItems(),
            };

            this.shouldShowSelectionsChangedPopupOnLeave = false;
        }

        // todo: do not remove! without it the consumption is not being updated
        this.enableDisableTip();

        // if user came from the services main view - init state
        this.methodSelector.setLoading();
        try {
            await TastierClient.payContext.prepareForCheckout();
        } catch (ex) {
            logError(ex);
        }

        this.checkLoginButtonVisibility();
        this.updateAmount();
        this.updateState();

        this.setApplyDiscountSectionVisibility();
    }

    protected onBecomeInvisible() {
        TastierClient.payContext.onAmountChanged.remove(this.updateAmount, this);
        TastierClient.payContext.onStateChanged.remove(this.updateState, this);
        TastierClient.payContext.onIntentChanged.remove(this.intentUpdated, this);
        TastierClient.identityContext.onIdentityChange.remove(this.onAuthenticationChanged, this);
        UserCreditCardsService.onCreditCardsChanged.remove(this.onCreditCardsChanged, this);
        TastierClient.context.onCartChanged.remove(this.onCartUpdated, this);
        dataEvents.appSocket.ItemSelectionChanged.remove(this.onItemSelectionChanged, this);

        TastierClient.context.onCartDiscountChanged.remove(this.onCartVoucherApplied, this);
        TastierClient.payContext.onBillDiscountChanged.remove(this.onBillDiscountChanged, this);

        //Reset pay button
        this.payBtn.setAmount(toMoneyWithSign(0));
    }

    private setEmailVisibility(forceHide: boolean = false) {
        const isAnonymousUserEmailRequired = this.isAnonymousUserEmailRequired();
        const isEndUserEmailRequired = this.isEndUserEmailRequired();

        if (!forceHide && (isAnonymousUserEmailRequired || isEndUserEmailRequired)) {
            this.$emailSection.show();
        } else {
            this.$emailSection.hide();
        }
    }

    private validateAndPay() {
        const isAnonymousUserEmailRequired = this.isAnonymousUserEmailRequired();
        const isEndUserEmailRequired = this.isEndUserEmailRequired();
        const isEmailRequired = isAnonymousUserEmailRequired || isEndUserEmailRequired;

        if (!isEmailRequired) {
            this.payForSelectedItems();
            return;
        }

        this.emailInput.setError(false);
        const email = this.emailInput.getValue();

        if (this.emailInput.isValid()) {
            this.showIsEmailCorrectPopup(email, isEndUserEmailRequired);
            return;
        } else {
            this.emailInput.setError(true);
            if (isAnonymousUserEmailRequired) {
                this.showProvideEmailPopup();
            } else {
                this.showProvideEndUserEmailPopup();
            }
        }
    }

    private setApplyDiscountSectionVisibility(forceHide: boolean = false) {
        const method = TastierClient.payContext.selectedPaymentMethod;
        const isPayService = Helper.isPayService();
        const showApplyDiscountSection = isPayService
            ? getIsApplyVouchersAllowedForPayUseCase(method, Helper.getPayUseCase())
            : getIsApplyVouchersAllowedForOrderUseCase(method, Helper.getCurrentOrderServiceUseCase());
        if (!forceHide && showApplyDiscountSection) {
            this.$applyDiscountSectionContainer.show();
        } else {
            this.$applyDiscountSectionContainer.hide();
        }
    }

    private isAnonymousUserEmailRequired() {
        const useCase = Helper.getCurrentOrderServiceUseCase();
        return useCase?.buyerRequirement === BuyerRequirement.Email && TastierClient.identityContext.isAnonymousUser();
    }

    private useVouchersAndDiscounts() {
        const useCase = Helper.getCurrentOrderServiceUseCase();
        return useCase?.useVouchersAndDiscounts === true;
    }

    private isEndUserEmailRequired() {
        const useCase = Helper.getCurrentOrderServiceUseCase();
        return (
            (useCase?.buyerRequirement === BuyerRequirement.Email ||
                useCase?.buyerRequirement === BuyerRequirement.SignedUp ||
                useCase?.buyerRequirement === BuyerRequirement.EmailConfirmed) &&
            TastierClient.identityContext.isWaiterOrAdmin()
        );
    }

    private payForSelectedItems(anonymousUserEmail?: string, endUserEmail?: string) {
        const selectedItems = TastierClient.context.getSelectedItems();
        TastierClient.payContext.pay(
            selectedItems,
            this.getConsumption(),
            false,
            () => {
                if (this.onGoBack) {
                    this.onGoBack();
                }
            },
            anonymousUserEmail,
            endUserEmail
        );
    }

    private showIsEmailCorrectPopup(email: string, isEndUserEmail: boolean) {
        Popup.show(
            translations.pay.isEmailCorrectQuestion.replace("{0}", email),
            () => {
                this.payForSelectedItems(isEndUserEmail ? undefined : email, isEndUserEmail ? email : undefined);
            },
            translations.general.yes,
            translations.general.cancel,
            "email-confirmation-popup",
            false,
            true,
            true
        );
    }

    private showProvideEmailPopup() {
        Popup.show(trans.pay.provideEmailMessage, undefined, trans.general.ok, undefined, "provide-email-popup");
    }

    private showProvideEndUserEmailPopup() {
        Popup.show(
            translations.pay.pleaseEnterEndUsersEmail,
            undefined,
            trans.general.ok,
            undefined,
            "provide-email-popup"
        );
    }

    private updateAmount() {
        // we calculate total here, because consumption should not change after entering this page!
        const currentConsumption = this.getConsumption();
        const currentDiscount = this.getDiscount();
        const total = Misc.roundCents(this.getTotalAfterDiscount() + TastierClient.payContext.tipAmount);
        this.payBtn.setAmount(toMoneyWithSign(total, true));
        this.$discountAmount.html((-1 * currentDiscount).toFixed(2));
        this.$consumptionAmt.html(currentConsumption.toFixed(2));
        this.$tipAmt.width(this.$consumptionAmt.width()).html(TastierClient.payContext.tipAmount.toFixed(2));

        // update visibility
        if (currentDiscount != 0) {
            this.$discountRow.show();
        } else {
            this.$discountRow.hide();
        }
    }

    private getConsumption() {
        return this.currentState?.consumption ?? 0;
    }

    private getDiscount() {
        return this.currentState?.discount ?? 0;
    }

    private getTotalAfterDiscount() {
        return this.currentState?.totalAfterDiscount ?? 0;
    }

    private updateState() {
        const isPayLater = TastierClient.payContext.isPayLater();

        this.updateTitle(isPayLater);
        this.updateMethodSelectorPaymentInProgress();
        this.updateButtonSize();

        if (!this.useTip || !TastierClient.payContext.isTipAllowedForSelectedPaymentMethod()) {
            this.enableDisableTip();
        }

        this.updatePayButtonState(isPayLater);
    }

    private updateTitle(isPayLater: boolean) {
        if (isPayLater) {
            this.$title.text(trans.pay.order);
        } else {
            this.$title.text(trans.pay.title);
        }
    }

    private updateMethodSelectorPaymentInProgress() {
        const isOpen = this.methodSelector.isOpen;
        const isPaying = TastierClient.payContext.state === PaymentState.Paying;
        const isOrdering = TastierClient.payContext.state === PaymentState.Ordering;
        const isOpenAndCardFormModeAndPaying = isOpen && this.methodSelector.isCardFormMode && isPaying;
        const isClosedAndPayingOrOrdering = !isOpen && (isPaying || isOrdering);

        this.methodSelector.paymentInProgress = isOpenAndCardFormModeAndPaying || isClosedAndPayingOrOrdering;
    }

    private updateButtonSize() {
        if (this.methodSelector.isOpen) {
            this.$bottomContainer.removeClass("moveaside");
        } else {
            this.$bottomContainer.addClass("moveaside");
        }
    }

    private updatePayButtonState(isPayLater: boolean) {
        if (this.methodSelector.isOpen) {
            if (!this.methodSelector.isCardFormMode) {
                this.payBtn.setDisabled();
                this.quickPaymentMethodSelector.setDisabled();
            } else {
                if (this.isPayingState()) {
                    this.payBtn.setDisabled(trans.pay.paying);
                    this.quickPaymentMethodSelector.setDisabled();
                } else if (isPayLater) {
                    this.quickPaymentMethodSelector.setEnabled();
                    this.payBtn.setEnabled();
                    this.payBtn.setAction(trans.pay.order);
                } else {
                    this.quickPaymentMethodSelector.setEnabled();
                    this.payBtn.setEnabled();
                    this.payBtn.setAction(translations.pay.payNow);
                }
            }
        } else {
            switch (TastierClient.payContext.state) {
                case PaymentState.Preparing:
                    this.payBtn.setDisabled();
                    this.payBtn.setAction(translations.pay.payNow);
                    break;
                case PaymentState.MethodSelection:
                    this.quickPaymentMethodSelector.setEnabled();
                    this.payBtn.setDisabled(undefined, true);
                    this.payBtn.setAction(translations.pay.payNow);
                    break;
                case PaymentState.ReadyToPay:
                    this.payBtn.setEnabled();
                    this.quickPaymentMethodSelector.setEnabled();
                    if (isPayLater) {
                        this.payBtn.setAction(trans.pay.order);
                    } else {
                        this.payBtn.setAction(translations.pay.payNow);
                    }
                    break;
                case PaymentState.Paying:
                    this.payBtn.setDisabled(trans.pay.paying);
                    this.quickPaymentMethodSelector.setDisabled();
                    break;
                case PaymentState.Confirmed:
                    this.payBtn.setDisabled(trans.pay.btnSuccessful);
                    this.quickPaymentMethodSelector.setDisabled();
                    break;
                case PaymentState.Failed:
                    this.payBtn.setDisabled(trans.pay.btnFailed);
                    this.quickPaymentMethodSelector.setDisabled();
                    break;
                case PaymentState.Ordering:
                    this.payBtn.setDisabled(trans.pay.ordering);
                    this.quickPaymentMethodSelector.setDisabled();
                    break;
            }
        }
    }

    private onItemSelectionChanged(eventData: BillItemSelectionChangedEvent) {
        if (eventData.identityId !== TastierClient.identityContext.getIdentityId()) {
            return;
        }

        if (eventData.reason == UserItemSelectionChangeReason.SelectionsReset) {
            this.shouldShowSelectionsChangedPopupOnLeave = true;
        }
    }

    private intentUpdated(intent: IPaymentIntent) {
        this.quickPaymentMethodSelector.setPaymentMethodButtons(intent.quickPaymentMethods);
        this.methodSelector.setAvailableOptions(intent.paymentMethods);
    }

    private async onAuthenticationChanged() {
        if (this.isPayingState()) {
            return;
        }

        await this.prepareForCheckout();
        this.checkLoginButtonVisibility();
    }

    private async onCreditCardsChanged() {
        if (this.isPayingState()) {
            return;
        }

        await this.prepareForCheckout();
    }

    private async prepareForCheckout() {
        this.methodSelector.setLoading();
        try {
            await TastierClient.payContext.prepareForCheckout();
        } catch (ex) {
            logError(ex);
        }
    }

    private isPayingState() {
        return TastierClient.payContext.state === PaymentState.Paying;
    }

    private checkLoginButtonVisibility() {
        const isAuthenticated = TastierClient.identityContext.isAuthenticated();
        const useCase = Helper.getCurrentOrderServiceUseCase();

        if (isAuthenticated || (useCase !== undefined && !useCase.enableSignUpBeforePay)) {
            this.$signinSection.hide();
        } else {
            this.$signinSection.show();
        }
    }

    private onCartVoucherApplied() {
        TastierClient.payContext.renewConsumption();
        this.currentState = {
            isPayService: false,
            consumption: TastierClient.payContext.consumption,
            totalAfterDiscount: TastierClient.payContext.totalExclTipAfterDiscounts,
            discount: TastierClient.payContext.discountAmount,
            selectedItems: TastierClient.context.getSelectedItems(),
        };

        this.updateAmount();
    }

    private onBillDiscountChanged() {
        TastierClient.payContext.renewConsumption();
        this.currentState = {
            isPayService: false,
            consumption: TastierClient.payContext.consumption,
            totalAfterDiscount: TastierClient.payContext.totalExclTipAfterDiscounts,
            discount: TastierClient.payContext.discountAmount,
            selectedItems: TastierClient.context.getSelectedItems(),
        };

        this.updateAmount();
    }

    private onCartUpdated() {
        // don't notify if cart is already checked out / payment going on
        const statesToSkip = [PaymentState.Paying, PaymentState.Confirmed, PaymentState.Failed];

        if (statesToSkip.includes(TastierClient.payContext.state)) return;

        Popup.show(
            "<b>" +
                Format.htmlEscape(translations.pay.cartChangedTitle) +
                "</b><br /><br />" +
                Format.htmlEscape(translations.pay.cartChangedMessage),
            () => {
                if (this.onGoBack !== undefined) this.onGoBack();
            },
            undefined,
            undefined,
            "totalChanged"
        );
    }

    private enableDisableTip() {
        const useCase = Helper.getCurrentUseCase();
        this.useTip = useCase?.useTip == true && TastierClient.payContext.isTipAllowedForSelectedPaymentMethod();

        if (this.useTip) {
            const newTipPercent = this.$tip.is(":visible")
                ? TastierClient.payContext.tipPercent
                : TastierClient.payContext.defaultTipPercent;

            this.$tip.show();
            this.$tipAmt.parent().parent().show();
            if (TastierClient.identityContext?.isWaiterInCurrentLocation()) {
                TastierClient.payContext.setTip(0);
                this.tipInput!.setValue();
                this.receivedInput!.setValue();
            } else {
                this.tipSlider!.setValue(newTipPercent!).resize();
                TastierClient.payContext.setTipInPercent(newTipPercent!);
            }
        } else {
            this.$tipAmt.parent().parent().hide();
            TastierClient.payContext.setTipInPercent(0);
            this.$tip.hide();
            this.tipSlider?.setValue(0);
            this.tipInput?.setValue(0);
        }
    }
}
